import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { Observable } from 'rxjs';
import { GainShareModel } from '../../models/customer-maintenance/invoice/gain-share.model';
import { environment } from '../../../../environments/environment';
import { MgmtFeeModel } from '../../models/customer-maintenance/invoice/mgmt-fee.model';
import { InterventionFeeModel } from '../../models/customer-maintenance/invoice/intervention-fee.model';
import { SubscriptionFeeModel } from '../../models/customer-maintenance/invoice/subscription-fee.model';
import { MgmtFeeFrequencyModel } from '../../models/customer-maintenance/invoice/mgmt-fee-frequency.model';
import { InterventionFeeEventModel } from '../../models/customer-maintenance/invoice/intervention-fee-event.model';
import { SubscriptionEventModel } from '../../models/customer-maintenance/invoice/subscription-event.model';
import { ProcessFeeModel } from '../../models/customer-maintenance/invoice/process-fee.model';
import { TransactionFeeModel } from '../../models/customer-maintenance/invoice/transaction-fee.model';
import { ExpenseCodeModel } from '../../models/customer-maintenance/invoice/expense-code.model';
import { SpecialExpenseCodeMapping } from '../../models/customer-maintenance/invoice/expense-code-mapping.model';
import { PaymentHistoryModel } from '../../models/customer-maintenance/invoice/payment-history.model';
import { InvoiceHistoryModel } from '../../models/customer-maintenance/invoice/invoice-history.model';
import { GeneralResponseModel } from "../../models/general-response.model";
import { CustomerInvoiceConfigModel } from "../../models/customer-maintenance/audit-trail/customer-invoice.model";
import { map } from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {

  constructor(private readonly apiService: ApiService) { }

  getGainShare(customerId?: number): Observable<GainShareModel[]> {
    let url = environment.hairball.invoicing.gainShare;
    if (customerId) { url = url + customerId; }
    return this.apiService.get<GainShareModel[]>(url);
  }

  getMgmtFee(customerId?: number): Observable<MgmtFeeModel[]> {
    let url = environment.hairball.invoicing.mgmtFee;
    if (customerId) { url = url + customerId; }
    return this.apiService.get<MgmtFeeModel[]>(url);
  }

  getInterventionFees(customerId?: number): Observable<InterventionFeeModel[]> {
    let url = environment.hairball.invoicing.interventionFee;
    if (customerId) { url = url + customerId; }
    return this.apiService.get<InterventionFeeModel[]>(url);
  }

  getSubscriptionFees(customerId?: number): Observable<SubscriptionFeeModel[]> {
    let url = environment.hairball.invoicing.subscriptionFee;
    if (customerId) { url = url + customerId; }
    return this.apiService.get<SubscriptionFeeModel[]>(url);
  }

  getMgmtFeeFrequency(): Observable<MgmtFeeFrequencyModel[]> {
    return this.apiService.get<MgmtFeeFrequencyModel[]>(environment.hairball.invoicing.mgmtFeeFrequency);
  }

  configMgmtFee(data: any, action: string): Observable<any> {
    if (action === 'add') {
      return this.apiService.post<any>(environment.hairball.invoicing.mgmtFeeConfig, data);
    } else {
      return this.apiService.put<any>(environment.hairball.invoicing.mgmtFeeConfig, data);
    }
  }

  configGainShareFee(data: any, action: string): Observable<any> {
    if (action === 'add') {
      return this.apiService.post<any>(environment.hairball.invoicing.gainShareConfig, data);
    } else {
      return this.apiService.put<any>(environment.hairball.invoicing.gainShareConfig, data);
    }
  }

  getInterventionFeeEvents(): Observable<InterventionFeeEventModel[]> {
    return this.apiService.get<InterventionFeeEventModel[]>(environment.hairball.invoicing.interventionFeeEvents);
  }

  configInterventionFee(data: any, action: string): Observable<any> {
    if (action === 'add') {
      return this.apiService.post<any>(environment.hairball.invoicing.interventionFeeConfig, data);
    } else {
      return this.apiService.put<any>(environment.hairball.invoicing.interventionFeeConfig, data);
    }
  }

  getSubscriptionFeeFrequency(): Observable<SubscriptionFeeModel[]> {
    return this.apiService.get<SubscriptionFeeModel[]>(environment.hairball.invoicing.subscriptionFeeFrequency);
  }

  getSubscriptionFeeEvents(): Observable<SubscriptionEventModel[]> {
    return this.apiService.get<SubscriptionEventModel[]>(environment.hairball.invoicing.subscriptionFeeEvents);
  }

  configSubscriptionFee(data: any, action: string): Observable<any> {
    if (action === 'add') {
      return this.apiService.post<any>(environment.hairball.invoicing.subscriptionFeeConfig, data);
    } else {
      return this.apiService.put<any>(environment.hairball.invoicing.subscriptionFeeConfig, data);
    }
  }

  getProcessFees(customerId: number): Observable<ProcessFeeModel[]> {
    const url = environment.hairball.invoicing.processFee + customerId;
    return this.apiService.get<ProcessFeeModel[]>(url);
  }

  getTransactionFees(customerId: number): Observable<TransactionFeeModel[]> {
    const url = environment.hairball.invoicing.transactionFee + customerId;
    return this.apiService.get<TransactionFeeModel[]>(url);
  }

  processFeeConfig(data: any, action: string): Observable<any> {
    if (action === 'add') {
      return this.apiService.post<any>(environment.hairball.invoicing.processFeeConfig, data);
    } else {
      return this.apiService.put<any>(environment.hairball.invoicing.processFeeConfig, data);
    }
  }

  transactionFeeConfig(data: any, action: string): Observable<any> {
    if (action === 'add') {
      return this.apiService.post<any>(environment.hairball.invoicing.transactionFeeConfig, data);
    } else {
      return this.apiService.put<any>(environment.hairball.invoicing.transactionFeeConfig, data);
    }
  }

  getExpenseCodes(customerId: number): Observable<ExpenseCodeModel[]> {
    return this.apiService.get<ExpenseCodeModel[]>(environment.hairball.expenseCodeByCustomerId + customerId, { active: false}).pipe(
      map((response: any) => response.Data)
      );
  }

  manageExpenseCode(data: any, action: string): Observable<any> {
    if (action === 'add') {
      return this.apiService.post<any>(environment.hairball.invoicing.expenseCode, data);
    } else {
      return this.apiService.put<any>(environment.hairball.invoicing.expenseCode, data);
    }
  }

  getExpenseCodeMapping(customerId: number): Observable<GeneralResponseModel<SpecialExpenseCodeMapping[]>> {
    return this.apiService.get<GeneralResponseModel<SpecialExpenseCodeMapping[]>>(environment.hairball.expenseCodeMappingByCustomer + customerId);
  }

  manageExpenseCodeMapping(data: any, action: string): Observable<any> {
    if (action === 'add') {
      return this.apiService.post<any>(environment.hairball.invoicing.createExpenseCodeMapping, data);
    } else {
      return this.apiService.put<any>(environment.hairball.invoicing.updateExpenseCodeMapping, data);
    }
  }

  getPaymentHistory(customerId: number): Observable<PaymentHistoryModel[]> {
    return this.apiService.get<PaymentHistoryModel[]>(environment.hairball.invoicing.paymentHistory + customerId);
  }

  getInvoiceHistory(customerId: number): Observable<InvoiceHistoryModel[]> {
    return this.apiService.get<InvoiceHistoryModel[]>(environment.hairball.invoicing.invoiceHistory + customerId);
  }

  manageForceZeroLogsSavings(data: any): Observable<any> {
    return this.apiService.put<any>(environment.hairball.invoicing.forceZeroLogsSavings, data);
  }

  getAllInvoiceConfigByCustomer(customerId: number): Observable<CustomerInvoiceConfigModel[]> {
    return this.apiService
      .get<CustomerInvoiceConfigModel[]>(environment.hairball.invoicing.customerInvoiceConfig.getAll + customerId);
  }

}
